// `?album=N` on the public 3D preview (/preview/:token).
//
// Parcel scanner plan, 2026-10-01: the scanner's order card opens one preview
// per album of a Duo/Trio as `previewUrl?album=N`. N is the album NUMBER — the
// one on the chip the customer sees (« Album 2 »), 1-based — never a position
// in the list: the list holds only the albums that are RENDERED, so on a Duo
// where album 1 is still being built, album 2 sits at position 0.
//
// Absent, malformed, out of range or not rendered yet → the first album, which
// is exactly what the page did before this parameter existed.
//
// Pure. Guard: previewAlbumParam.test.ts

/** The requested album number (1-based), or null when absent or not a whole number from 1 up. */
export function parseAlbumParam(search: string): number | null {
  const raw = new URLSearchParams(search).get('album');
  if (raw == null || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n >= 1 ? n : null;
}

/** Index into the rendered albums for the requested album number; 0 (the first) otherwise. */
export function initialAlbumIdx(albums: ReadonlyArray<{ album_index: number }>, requested: number | null): number {
  if (requested == null) return 0;
  const i = albums.findIndex((a) => a.album_index === requested);
  return i >= 0 ? i : 0;
}

/**
 * `?page=N` (owner, 2026-10-07: « click "Preview · p. 50" opens the 3D preview
 * at that page »). N is the page number the preview itself shows (« Pages
 * 50–51 sur 60 ») — the same number a client note carries. null = absent or
 * not a whole number from 1 up: the preview opens on its landing, as before.
 */
export function parsePageParam(search: string): number | null {
  const raw = new URLSearchParams(search).get('page');
  if (raw == null || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n >= 1 ? n : null;
}

/** The flipbook index of page N: 0 = cover, 1 = inside cover, page N = N + 1 (clamped to the book). */
export function flipIndexForPage(page: number, pageTotal: number): number {
  return Math.max(0, Math.min(pageTotal - 1, page + 1));
}

/** The admin link to the preview at a page (and album, for a Duo/Trio). */
export function previewPageHref(previewUrl: string, page: number | null, albumIndex: number | null): string {
  try {
    const u = new URL(previewUrl, 'https://app.storiz.ma');
    if (albumIndex && albumIndex > 1) u.searchParams.set('album', String(albumIndex));
    if (page) u.searchParams.set('page', String(page));
    return /^https?:/i.test(previewUrl) ? u.toString() : `${u.pathname}${u.search}`;
  } catch {
    return previewUrl;
  }
}
